import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Upload, FileText, CheckCircle, XCircle, Clock, Loader2, ChevronRight, ChevronLeft, Calendar, Trash2, AlertTriangle, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/currency';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';

const fmt = (n) => fmtMoney(n, null, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const getML = (y, m) => `${MN[parseInt(m, 10) - 1]} ${y}`;
// Build a "prev-curr year" pair label for statement periods like "Apr–May 2026" or "Dec 2025–Jan 2026"
const getPairLabel = (y, m) => {
  const yi = parseInt(y, 10), mi = parseInt(m, 10);
  const prevMi = mi === 1 ? 12 : mi - 1;
  const prevYi = mi === 1 ? yi - 1 : yi;
  if (prevYi === yi) return `${MN[prevMi - 1]}–${MN[mi - 1]} ${yi}`;
  return `${MN[prevMi - 1]} ${prevYi}–${MN[mi - 1]} ${yi}`;
};
const navM = (y, m, d) => { let mi = parseInt(m, 10) + d, yi = parseInt(y, 10); while (mi > 12) { mi -= 12; yi++; } while (mi < 1) { mi += 12; yi--; } return { year: String(yi), month: String(mi).padStart(2, '0') }; };

export default function UploadPage() {
  useDisplayCurrency(); // re-render when user changes base currency
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedStmt, setExpandedStmt] = useState(null);
  const [stmtTxns, setStmtTxns] = useState({});
  const [targetMonth, setTargetMonth] = useState('');
  const [overrideCurrency, setOverrideCurrency] = useState(''); // '' = use user's base_currency
  const [uploadMonthFilter, setUploadMonthFilter] = useState('all'); // 'all' | 'YYYY-MM'

  // Distinct upload months derived from statements (sorted desc)
  const uploadMonths = (() => {
    const set = new Set();
    statements.forEach((s) => {
      if (s.upload_date) set.add(s.upload_date.slice(0, 7));
    });
    return Array.from(set).sort((a, b) => b.localeCompare(a));
  })();

  const filteredStatements = uploadMonthFilter === 'all'
    ? statements
    : statements.filter((s) => (s.upload_date || '').startsWith(uploadMonthFilter));

  // Generate month-pair options: 6 month-pairs back + current + 6 forward (value = end month, label = "Apr–May 2026")
  const monthOptions = (() => {
    const opts = [];
    const now = new Date();
    for (let i = -6; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      opts.push({ value: `${y}-${m}`, label: getPairLabel(String(y), m) });
    }
    return opts;
  })();

  const loadStatements = useCallback(async () => {
    try { const { data } = await api.get('/api/statements'); setStatements(data); } catch (err) { console.warn('[uploads] list statements failed:', err?.message || err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const onDrop = useCallback(async (files) => {
    if (files.length === 0) return;
    setUploading(true); setUploadResult(null);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      if (targetMonth) formData.append('target_month', targetMonth);
      if (overrideCurrency) formData.append('override_currency', overrideCurrency);
      try {
        const { data } = await api.post('/api/statements/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });

        // Guard: backend found this exact file already uploaded (non-deleted copy)
        if (data.duplicate) {
          setUploadResult({
            status: 'duplicate',
            filename: data.existing_filename || file.name,
            uploaded_at: data.uploaded_at,
            existing_id: data.existing_id,
          });
          setUploading(false);
          loadStatements();
          return;
        }
        
        // Poll for processing completion
        const statementId = data.statement_id;
        let pollCount = 0;
        const maxPolls = 30; // 30 seconds max
        
        const pollStatus = async () => {
          try {
            const { data: statements } = await api.get('/api/statements');
            const statement = statements.find(s => s._id === statementId);
            
            if (statement && statement.status === 'completed') {
              setUploadResult({
                ...data,
                status: 'completed',
                total_extracted: statement.total_extracted || statement.transaction_count || 0,
                inserted: (statement.transaction_count || 0) - (statement.duplicates || 0),
                duplicates: statement.duplicates || 0
              });
              toast.success(`${statement.transaction_count || 0} transactions extracted from ${file.name}`);
              return true;
            } else if (statement && statement.status === 'failed') {
              setUploadResult({ status: 'failed', error: statement.error || 'Processing failed' });
              toast.error('Statement processing failed');
              return true;
            }
            return false;
          } catch (err) {
            console.error('Poll error:', err);
            return false;
          }
        };
        
        // Initial poll after short delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        while (pollCount < maxPolls) {
          const done = await pollStatus();
          if (done) break;
          pollCount++;
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
        
        if (pollCount >= maxPolls) {
          setUploadResult({ ...data, status: 'processing' });
          toast.info('Statement is processing in the background');
        }
        
      } catch (err) {
        const msg = err.response?.data?.detail || 'Upload failed';
        toast.error(msg); setUploadResult({ status: 'failed', error: msg });
      }
    }
    setUploading(false); loadStatements();
  }, [loadStatements, targetMonth]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, accept: { 'application/pdf': ['.pdf'], 'text/csv': ['.csv'] }, maxSize: 10 * 1024 * 1024, disabled: uploading,
  });

  const toggleStmtExpand = async (stmtId) => {
    if (expandedStmt === stmtId) { setExpandedStmt(null); return; }
    setExpandedStmt(stmtId);
    if (!stmtTxns[stmtId]) {
      try { const { data } = await api.get(`/api/statements/${stmtId}/transactions`); setStmtTxns((p) => ({ ...p, [stmtId]: data })); }
      catch { toast.error('Failed to load transactions'); }
    }
  };

  const handleDeleteStatement = async (stmtId, filename) => {
    if (!window.confirm(`Delete "${filename}" and ALL its transactions?`)) return;
    try {
      const { data } = await api.delete(`/api/statements/${stmtId}`);
      toast.success(data.message);
      loadStatements();
    } catch { toast.error('Failed to delete'); }
  };

  const groupByMonth = (txns) => {
    const groups = {};
    (txns || []).forEach((t) => { const m = t.date?.slice(0, 7) || 'Unknown'; if (!groups[m]) groups[m] = []; groups[m].push(t); });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  };

  return (
    <div className="space-y-6" data-testid="upload-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Upload Statements</h1>
        <p className="text-sm text-muted-foreground mt-1">Import your credit card statements as PDF or CSV.</p>
      </div>

      {/* Upload rules banner */}
      <div className="p-3 rounded-md border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-900/10 text-amber-900 dark:text-amber-300 flex gap-2 items-start" data-testid="upload-cc-only-banner">
        <span className="text-base leading-none mt-0.5">⚠️</span>
        <div className="text-xs leading-relaxed">
          <strong>Credit Card statements only.</strong> Debit, chequing, savings statements and non-financial files (e.g. PDFs of stories, reports, or other documents) are automatically detected and rejected with a clear message.
        </div>
      </div>

      <Card className="border border-border shadow-none">
        <CardContent className="p-6">
          {/* Statement period selector */}
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-muted-foreground" />
              <Label className="text-sm font-medium">Statement period:</Label>
            </div>
            <Select value={targetMonth || 'auto'} onValueChange={(v) => setTargetMonth(v === 'auto' ? '' : v)}>
              <SelectTrigger className="w-[220px] h-9" data-testid="upload-month-selector">
                <SelectValue placeholder="Auto-detect from statement" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-detect from statement</SelectItem>
                {monthOptions.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {targetMonth && <Badge variant="outline" className="text-xs">Transactions tagged to {getPairLabel(targetMonth.split('-')[0], targetMonth.split('-')[1])}</Badge>}
          </div>

          {/* Currency override selector */}
          <div className="mb-5 flex items-center gap-3 flex-wrap">
            <Label className="text-sm font-medium text-muted-foreground">Currency:</Label>
            <Select value={overrideCurrency || 'default'} onValueChange={(v) => setOverrideCurrency(v === 'default' ? '' : v)}>
              <SelectTrigger className="w-[200px] h-9" data-testid="upload-currency-selector">
                <SelectValue placeholder="Use account default" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="default">Use account default</SelectItem>
                <SelectItem value="CAD">CAD — Canadian Dollar</SelectItem>
                <SelectItem value="USD">USD — US Dollar</SelectItem>
                <SelectItem value="INR">INR — Indian Rupee</SelectItem>
                <SelectItem value="GBP">GBP — British Pound</SelectItem>
                <SelectItem value="EUR">EUR — Euro</SelectItem>
                <SelectItem value="AUD">AUD — Australian Dollar</SelectItem>
                <SelectItem value="AED">AED — UAE Dirham</SelectItem>
                <SelectItem value="SGD">SGD — Singapore Dollar</SelectItem>
              </SelectContent>
            </Select>
            {overrideCurrency
              ? <Badge variant="secondary" className="text-xs">Transactions tagged as {overrideCurrency}</Badge>
              : <span className="text-xs text-muted-foreground">Defaults to your account currency</span>}
          </div>

          <div {...getRootProps()} data-testid="upload-dropzone" className={`border-2 border-dashed rounded-md p-10 text-center cursor-pointer transition-colors ${isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}>
            <input {...getInputProps()} />
            <div className="flex flex-col items-center gap-3">
              {uploading ? <Loader2 className="h-10 w-10 text-primary animate-spin" /> : <Upload className="h-10 w-10 text-muted-foreground" />}
              <div>
                <p className="font-medium">{uploading ? 'Processing...' : isDragActive ? 'Drop files here' : 'Drag & drop statements here'}</p>
                <p className="text-sm text-muted-foreground mt-1">PDF and CSV (max 10MB)</p>
              </div>
              {!uploading && <Button variant="outline" size="sm" className="mt-2">Browse Files</Button>}
            </div>
          </div>

          {uploading && <div className="mt-4"><Progress value={50} className="h-1" /><p className="text-xs text-muted-foreground mt-2">Extracting and categorizing...</p></div>}

          {uploadResult && !uploading && (
            <>
              {/* ── Duplicate detected banner ── */}
              {uploadResult.status === 'duplicate' && (
                <div className="mt-4 p-4 rounded-md border border-amber-300 bg-amber-50 dark:border-amber-700/50 dark:bg-amber-900/15" data-testid="upload-duplicate-banner">
                  <div className="flex items-start gap-3">
                    <Copy className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-sm text-amber-900 dark:text-amber-300">Duplicate Statement Detected</p>
                      <p className="text-sm text-amber-800 dark:text-amber-400 mt-1">
                        <span className="font-medium truncate">"{uploadResult.filename}"</span> was already uploaded
                        {uploadResult.uploaded_at && (
                          <> on <span className="font-medium">{new Date(uploadResult.uploaded_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span></>
                        )}.
                      </p>
                      <p className="text-xs text-amber-700 dark:text-amber-500 mt-1.5">
                        Find it in <strong>Upload History</strong> below. To re-import it, delete the existing copy first, then upload again.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        className="mt-2.5 h-7 text-xs border-amber-400 text-amber-800 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/30"
                        onClick={() => {
                          const el = document.querySelector('[data-testid="upload-history-card"]');
                          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                          if (uploadResult.existing_id) {
                            setTimeout(() => {
                              const row = document.querySelector(`[data-testid="statement-${uploadResult.existing_id}"]`);
                              if (row) { row.classList.add('ring-2', 'ring-amber-400'); setTimeout(() => row.classList.remove('ring-2', 'ring-amber-400'), 3000); }
                            }, 400);
                          }
                        }}
                      >
                        Show in Upload History
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Error / Success banner ── */}
              {uploadResult.status !== 'duplicate' && (
                <div className={`mt-4 p-4 rounded-md border ${
                  uploadResult.status === 'failed'
                    ? 'border-destructive/30 bg-destructive/5'
                    : 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/10'
                }`} data-testid="upload-result">
                  <div className="flex items-center gap-2 mb-2">
                    {uploadResult.status === 'failed'
                      ? <XCircle className="h-4 w-4 text-destructive" />
                      : <CheckCircle className="h-4 w-4 text-green-600" />}
                    <span className="font-medium text-sm">
                      {uploadResult.status === 'failed' ? 'Upload Rejected' : 'Upload Successful'}
                    </span>
                  </div>
                  {uploadResult.status === 'failed' && uploadResult.error && (
                    <p className="text-sm text-destructive/80">{uploadResult.error}</p>
                  )}
                  {uploadResult.status !== 'failed' && (
                    <div className="text-sm space-y-1 text-muted-foreground">
                      <p>Extracted: {uploadResult.total_extracted} | New: {uploadResult.inserted} | Duplicates: {uploadResult.duplicates}</p>
                      <Button variant="link" size="sm" className="h-auto p-0 text-primary" onClick={() => navigate('/transactions')}>View Transactions <ChevronRight className="h-3 w-3 ml-1" /></Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <Card className="border border-border shadow-none" data-testid="upload-history-card">
        <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-sm font-medium">Upload History {uploadMonthFilter !== 'all' && <span className="text-xs font-normal text-muted-foreground ml-2">({filteredStatements.length} of {statements.length})</span>}</CardTitle>
          <Select value={uploadMonthFilter} onValueChange={setUploadMonthFilter}>
            <SelectTrigger className="w-[170px] h-8 text-xs" data-testid="upload-month-filter">
              <Calendar className="h-3.5 w-3.5 mr-1.5" />
              <SelectValue placeholder="All months" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All months</SelectItem>
              {uploadMonths.map((ym) => {
                const [y, m] = ym.split('-');
                return <SelectItem key={ym} value={ym}>{getML(y, m)}</SelectItem>;
              })}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? <div className="space-y-2">{[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-muted rounded animate-pulse" />)}</div>
          : filteredStatements.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">{uploadMonthFilter === 'all' ? 'No statements uploaded yet' : 'No uploads in this month'}</p>
          : (
            <div className="space-y-2">
              {filteredStatements.map((s) => (
                <div key={s._id}>
                  <div className="flex items-center justify-between p-3 rounded-md border border-border bg-background cursor-pointer hover:bg-accent/30 transition-colors" onClick={() => toggleStmtExpand(s._id)} data-testid={`statement-${s._id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate" data-testid={`statement-display-name-${s._id}`}>
                          {s.target_month
                            ? `Statement — ${getPairLabel(s.target_month.split('-')[0], s.target_month.split('-')[1])}`
                            : (s.filename || 'Statement')}
                        </p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className="text-[11px] text-muted-foreground truncate max-w-[200px]" title={s.filename}>{s.filename}</span>
                          <span className="text-[11px] text-muted-foreground">·</span>
                          <span className="text-[11px] text-muted-foreground">{new Date(s.upload_date).toLocaleDateString()}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Badge variant="secondary" className="text-xs">{s.transaction_count} txns</Badge>
                      {s.status === 'completed' ? <CheckCircle className="h-4 w-4 text-green-500" /> : s.status === 'failed' ? <XCircle className="h-4 w-4 text-red-500" /> : <Clock className="h-4 w-4 text-muted-foreground" />}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={(e) => { e.stopPropagation(); handleDeleteStatement(s._id, s.filename); }} data-testid={`delete-stmt-upload-${s._id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                      <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform ${expandedStmt === s._id ? 'rotate-90' : ''}`} />
                    </div>
                  </div>
                  {expandedStmt === s._id && (
                    <div className="ml-7 mt-2 mb-3 space-y-3">
                      {stmtTxns[s._id] ? groupByMonth(stmtTxns[s._id]).map(([month, txns]) => {
                        const total = txns.filter(t => t.transaction_type === 'debit').reduce((sum, t) => sum + t.amount, 0);
                        return (
                          <div key={month} className="border border-border rounded-md p-3">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2"><Badge variant="outline" className="text-xs">{month}</Badge><span className="text-xs text-muted-foreground">{txns.length} txns</span></div>
                              <div className="flex items-center gap-2"><span className="text-sm font-medium tabular-nums">{fmt(total)}</span><Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => navigate(`/transactions?year=${month.split('-')[0]}&month=${month.split('-')[1]}`)}>View <ChevronRight className="h-3 w-3 ml-0.5" /></Button></div>
                            </div>
                            <div className="space-y-1">{txns.slice(0, 5).map((t) => (
                              <div key={t._id} className="flex justify-between text-xs">
                                <div className="flex items-center gap-2 min-w-0"><span className="tabular-nums text-muted-foreground w-[70px]">{t.date}</span><span className="truncate">{t.normalized_merchant || t.merchant}</span></div>
                                <span className={`tabular-nums shrink-0 ${t.transaction_type === 'credit' ? 'text-green-600 dark:text-green-400' : ''}`}>{t.transaction_type === 'credit' ? '+' : '-'}{fmt(t.amount)}</span>
                              </div>
                            ))}{txns.length > 5 && <p className="text-xs text-muted-foreground text-center mt-1">+{txns.length - 5} more</p>}</div>
                          </div>
                        );
                      }) : <div className="flex items-center justify-center py-4"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
